// src/services/preferencesService.js
import { apiRequest } from "./apiService";
import { getLaunchNicheMode } from "../utils/discoverySegment";

const DEFAULT_PLATFORM_SLUG = "instagram";
const DEFAULT_CITY_SLUG = "london";
const DEFAULT_NICHE_SLUG = "food_reviewers";

const toStr = (value) =>
  String(value ?? "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim();

const toSlug = (value) =>
  toStr(value)
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[_-]+/g, " ")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\s+/g, "_");

const titleFromSlug = (slug) =>
  toStr(slug)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const uniq = (values) => {
  const seen = new Set();
  const out = [];

  for (const value of Array.isArray(values) ? values : []) {
    const text = toStr(value);
    if (!text) continue;

    const key = text.toLowerCase();
    if (seen.has(key)) continue;

    seen.add(key);
    out.push(text);
  }

  return out;
};

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

const normaliseArray = (value, { slug = true, lower = true } = {}) => {
  if (!Array.isArray(value)) return undefined;

  return uniq(
    value
      .map((item) => {
        if (slug) return toSlug(item);
        if (lower) return toStr(item).toLowerCase();
        return toStr(item);
      })
      .filter(Boolean)
  );
};

const normalisePlatforms = (value) => {
  const arr = normaliseArray(value, { slug: true });
  if (!Array.isArray(arr)) return undefined;

  return arr.map(() => DEFAULT_PLATFORM_SLUG).filter((v, i, a) => a.indexOf(v) === i);
};

const normaliseCities = (value) => {
  const arr = normaliseArray(value, { slug: true });
  if (!Array.isArray(arr)) return undefined;

  return arr.length ? arr : [];
};

const normaliseNiches = (value) => {
  const arr = normaliseArray(value, { slug: true });
  if (!Array.isArray(arr)) return undefined;

  return arr.length ? arr : [];
};

const normaliseExcludedTags = (value) => {
  const arr = normaliseArray(value, { slug: false, lower: true });
  if (!Array.isArray(arr)) return undefined;

  return arr.map((tag) => tag.replace(/^#+/, "")).filter(Boolean);
};

const normaliseCitySelection = (selection) => {
  if (!selection) return null;

  const src = typeof selection === "object" ? selection : { slug: selection };

  const slug = toSlug(
    src.slug ||
      src.city_slug ||
      src.citySlug ||
      src.id ||
      src.value ||
      DEFAULT_CITY_SLUG
  );

  if (!slug) return null;

  const name = toStr(src.name || src.label);

  return {
    slug,
    name: name || titleFromSlug(slug),
    isDefault: Boolean(src.isDefault || src.is_default),
  };
};

const normaliseCitySelections = (value, fallbackCities = []) => {
  const source = Array.isArray(value) ? value : [];
  const seen = new Set();
  const out = [];

  for (const item of source) {
    const selection = normaliseCitySelection(item);
    if (!selection?.slug || seen.has(selection.slug)) continue;

    seen.add(selection.slug);
    out.push(selection);
  }

  for (const slug of Array.isArray(fallbackCities) ? fallbackCities : []) {
    const selection = normaliseCitySelection({ slug });
    if (!selection?.slug || seen.has(selection.slug)) continue;

    seen.add(selection.slug);
    out.push(selection);
  }

  return out.map((item, index) => ({
    ...item,
    isDefault: index === 0,
  }));
};

const normaliseNicheSelection = (selection) => {
  if (!selection) return null;

  const src = typeof selection === "object" ? selection : { slug: selection };

  const slug = toSlug(
    src.slug ||
      src.niche_slug ||
      src.nicheSlug ||
      src.id ||
      src.value ||
      DEFAULT_NICHE_SLUG
  );

  if (!slug) return null;

  const name = toStr(src.name || src.label);
  const parent = toSlug(src.parent) || slug;
  const requestedMode = src.mode || src.niche_mode || src.nicheMode;
  const mode = getLaunchNicheMode(slug, requestedMode);

  return {
    slug,
    name: name || titleFromSlug(slug),
    mode,
    parent,
    isDefault: Boolean(src.isDefault || src.is_default),
  };
};

const normaliseNicheSelections = (value, fallbackNiches = []) => {
  const source = Array.isArray(value) ? value : [];
  const seen = new Set();
  const out = [];

  for (const item of source) {
    const selection = normaliseNicheSelection(item);
    if (!selection?.slug || seen.has(selection.slug)) continue;

    seen.add(selection.slug);
    out.push(selection);
  }

  for (const slug of Array.isArray(fallbackNiches) ? fallbackNiches : []) {
    const selection = normaliseNicheSelection({ slug });
    if (!selection?.slug || seen.has(selection.slug)) continue;

    seen.add(selection.slug);
    out.push(selection);
  }

  return out.map((item, index) => ({
    ...item,
    isDefault: index === 0,
  }));
};

const buildFeedDefaults = (payload, body) => {
  const feedDefaults =
    payload?.feedDefaults && typeof payload.feedDefaults === "object"
      ? payload.feedDefaults
      : {};

  const feedDefaultsSnake =
    payload?.feed_defaults && typeof payload.feed_defaults === "object"
      ? payload.feed_defaults
      : {};

  const city =
    toSlug(
      feedDefaults.city ||
        feedDefaultsSnake.city ||
        payload.activeCitySlug ||
        payload.active_city_slug ||
        payload.selectedCitySlug ||
        payload.selected_city_slug ||
        payload.primaryCitySlug ||
        payload.primary_city_slug ||
        payload.citySlug ||
        payload.city_slug ||
        body.cities?.[0] ||
        DEFAULT_CITY_SLUG
    ) || DEFAULT_CITY_SLUG;

  const niche =
    toSlug(
      feedDefaults.niche ||
        feedDefaultsSnake.niche ||
        payload.activeNicheSlug ||
        payload.active_niche_slug ||
        payload.selectedNicheSlug ||
        payload.selected_niche_slug ||
        payload.primaryNicheSlug ||
        payload.primary_niche_slug ||
        payload.nicheSlug ||
        payload.niche_slug ||
        body.niches?.[0] ||
        DEFAULT_NICHE_SLUG
    ) || DEFAULT_NICHE_SLUG;

  const requestedNicheMode =
    feedDefaults.nicheMode ||
    feedDefaultsSnake.niche_mode ||
    payload.nicheMode ||
    payload.niche_mode ||
    body.nicheSelections?.[0]?.mode;

  const nicheMode = getLaunchNicheMode(niche, requestedNicheMode);

  return {
    platform: DEFAULT_PLATFORM_SLUG,
    city,
    niche,
    nicheMode,
  };
};

export const getMyPreferences = () => apiRequest("/me/preferences", "GET");

export const updateMyPreferences = (payload = {}) => {
  if (!payload || typeof payload !== "object") {
    return Promise.reject(new Error("Valid payload is required to update preferences."));
  }

  const body = {};

  if (hasOwn(payload, "platforms")) {
    body.platforms = normalisePlatforms(payload.platforms);
  }

  if (hasOwn(payload, "cities")) {
    body.cities = normaliseCities(payload.cities);
  }

  if (hasOwn(payload, "citySelections") || hasOwn(payload, "city_selections")) {
    const fallbackCities = Array.isArray(body.cities)
      ? body.cities
      : normaliseCities(payload.cities) || [DEFAULT_CITY_SLUG];

    body.citySelections = normaliseCitySelections(
      payload.citySelections ?? payload.city_selections,
      fallbackCities
    );
  } else if (Array.isArray(body.cities) && body.cities.length) {
    body.citySelections = normaliseCitySelections([], body.cities);
  }

  if (hasOwn(payload, "niches")) {
    body.niches = normaliseNiches(payload.niches);
  }

  if (hasOwn(payload, "nicheSelections") || hasOwn(payload, "niche_selections")) {
    const fallbackNiches = Array.isArray(body.niches)
      ? body.niches
      : normaliseNiches(payload.niches) || [DEFAULT_NICHE_SLUG];

    body.nicheSelections = normaliseNicheSelections(
      payload.nicheSelections ?? payload.niche_selections,
      fallbackNiches
    );
  } else if (Array.isArray(body.niches) && body.niches.length) {
    body.nicheSelections = normaliseNicheSelections([], body.niches);
  }

  if (hasOwn(payload, "excludedTags") || hasOwn(payload, "excluded_tags")) {
    body.excludedTags = normaliseExcludedTags(payload.excludedTags ?? payload.excluded_tags);
  }

  Object.keys(body).forEach((key) => {
    if (body[key] === undefined) {
      delete body[key];
    }
  });

  if (!Array.isArray(body.platforms)) {
    body.platforms = [DEFAULT_PLATFORM_SLUG];
  }

  if (!Array.isArray(body.cities)) {
    body.cities = [DEFAULT_CITY_SLUG];
  }

  if (!Array.isArray(body.citySelections) || !body.citySelections.length) {
    body.citySelections = normaliseCitySelections([], body.cities);
  }

  if (!Array.isArray(body.niches)) {
    body.niches = [DEFAULT_NICHE_SLUG];
  }

  if (!Array.isArray(body.nicheSelections) || !body.nicheSelections.length) {
    body.nicheSelections = normaliseNicheSelections([], body.niches);
  }

  if (!Array.isArray(body.excludedTags)) {
    body.excludedTags = [];
  }

  const feedDefaults = buildFeedDefaults(payload, body);
  const primaryCity = body.citySelections[0] || normaliseCitySelection(feedDefaults.city);
  const primaryNiche = body.nicheSelections[0] || normaliseNicheSelection(feedDefaults.niche);

  body.citySelections = body.citySelections.map((item, index) => ({
    ...item,
    isDefault: index === 0,
  }));

  body.nicheSelections = body.nicheSelections.map((item, index) => ({
    ...item,
    isDefault: index === 0,
  }));

  body.city_selections = body.citySelections;
  body.niche_selections = body.nicheSelections;
  body.excluded_tags = body.excludedTags;

  body.activeCitySlug = primaryCity?.slug || feedDefaults.city;
  body.active_city_slug = body.activeCitySlug;
  body.selectedCitySlug = body.activeCitySlug;
  body.selected_city_slug = body.activeCitySlug;
  body.primaryCitySlug = body.activeCitySlug;
  body.primary_city_slug = body.activeCitySlug;
  body.citySlug = body.activeCitySlug;
  body.city_slug = body.activeCitySlug;
  body.locationSlug = body.activeCitySlug;
  body.location_slug = body.activeCitySlug;
  body.location = body.activeCitySlug;

  body.selectedCity = primaryCity;
  body.selected_city = primaryCity;
  body.primaryCity = primaryCity;
  body.primary_city = primaryCity;

  body.activeNicheSlug = primaryNiche?.slug || feedDefaults.niche;
  body.active_niche_slug = body.activeNicheSlug;
  body.selectedNicheSlug = body.activeNicheSlug;
  body.selected_niche_slug = body.activeNicheSlug;
  body.primaryNicheSlug = body.activeNicheSlug;
  body.primary_niche_slug = body.activeNicheSlug;
  body.nicheSlug = body.activeNicheSlug;
  body.niche_slug = body.activeNicheSlug;

  body.selectedNiche = primaryNiche;
  body.selected_niche = primaryNiche;
  body.primaryNiche = primaryNiche;
  body.primary_niche = primaryNiche;

  body.nicheMode = primaryNiche?.mode || feedDefaults.nicheMode || "child";
  body.niche_mode = body.nicheMode;

  body.feedDefaults = {
    platform: DEFAULT_PLATFORM_SLUG,
    city: body.activeCitySlug,
    niche: body.activeNicheSlug,
    nicheMode: body.nicheMode,
  };

  body.feed_defaults = {
    platform: DEFAULT_PLATFORM_SLUG,
    city: body.activeCitySlug,
    niche: body.activeNicheSlug,
    niche_mode: body.nicheMode,
  };

  return apiRequest("/me/preferences", "PUT", body);
};